<script setup>
import { RefreshIcon } from "@heroicons/vue/outline";
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  type: {
    type: String,
    default: "primary-1",
    validate: (value) =>
      [
        "primary-1",
        "primary-2",
        "primary-3",
        "primary-4",
        "secondary-1",
      ].includes(value),
  },
  size: {
    type: String,
    default: "md",
    validate: (value) => ["sm", "md", "lg"].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: "Loading...",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  outline: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(["click"]);

const type = computed(() => props.type);
const loading = computed(() => props.loading);
const loadingText = computed(() => props.loadingText);
const size = computed(() => props.size);
const disabled = computed(() => props.disabled);
const outline = computed(() => props.outline);

const handleClick = (event) => {
  if (!disabled.value && !loading.value) {
    emit("click", event);
  }
};
</script>
<template>
  <button
    v-bind="$attrs"
    :class="{
      'bg-kintz-primary-1 text-kintz-primary-4 border border-kintz-primary-3': type === 'primary-1' && !disabled && !outline,
      'bg-kintz-primary-2 text-kintz-primary-1 border border-kintz-primary-4': type === 'primary-2' && !disabled && !outline,
      'bg-kintz-primary-3 text-kintz-primary-4 border border-kintz-primary-4': type === 'primary-3' && !disabled && !outline,
      'bg-kintz-primary-4 text-kintz-primary-1 border border-kintz-primary-3': type === 'primary-4' && !disabled && !outline,
      'bg-kintz-secondary-1 text-white border border-kintz-primary-3': type === 'secondary-1' && !disabled && !outline,
      'bg-kintz-secondary-2 text-white border border-kintz-primary-3': type === 'secondary-2' && !disabled && !outline,
      'bg-kintz-secondary-3 text-white border border-kintz-primary-3': type === 'secondary-3' && !disabled && !outline,
      // outline styles
      'border border-kintz-primary-1 text-kintz-primary-1 bg-transparent': type === 'primary-1' && outline,
      // end of outline styles
      'px-4 py-2.5 rounded-lg text-sm font-normal': size === 'lg',
      'px-4 py-1.5 rounded-md text-sm font-normal': size === 'md',
      'px-3 py-1 rounded-sm text-xs font-normal': size === 'sm',
      'cursor-not-allowed bg-kintz-primary-4/30': loading || disabled,
    }"
    class="flex gap-1 lg:gap-2 justify-center items-center max-h-12 truncate"
    :disabled="loading || disabled"
    @click="handleClick"
  >
    <RefreshIcon
      v-if="loading"
      class="animate-spin-reverse h-5 w-5"
      aria-hidden="true"
    />
    <slot v-if="!loading"></slot>
    <span v-if="loading">{{ loadingText }}</span>
  </button>
</template>

<style scoped></style>
